import type { ReactElement } from "react";
import type { PartnerRole } from "@kundeninfo/api-client";
import { Optionen } from "@kundeninfo/ui/me-design";
import { PARTNER_TEXT } from "./texts.js";
import { ROLLEN } from "./rollenFilter.js";

/**
 * Die ROLLENWAHL beim Anlegen eines Geschäftspartners und beim Zuordnen eines weiteren Unternehmens (Festlegung C9,
 * P2): Kunde bzw. Interessent (eine Stufe derselben Rolle, schliessen sich aus) und Lieferant, mindestens eine.
 */
export type RoleChoice = { customer: "ACTIVE" | "PROSPECT" | null; supplier: boolean };

const CUSTOMER_STAGE: Record<"CUSTOMER" | "PROSPECT", "ACTIVE" | "PROSPECT"> = { CUSTOMER: "ACTIVE", PROSPECT: "PROSPECT" };

/**
 * Die Vorgabe aus den aktiven Chips der Liste. Sind alle drei an, ist die Liste UNEINGESCHRÄNKT — dann gilt die
 * Vorgabe des Bestands (Interessent), sonst entstünde jeder neue Partner zugleich als Lieferant. Sonst: Lieferant,
 * wenn „Lieferanten“ an ist; Interessent vor Kunde, wenn beide an sind (wie die Bestandsvorgabe).
 */
export function roleChoiceFrom(roles: readonly PartnerRole[]): RoleChoice {
  if (roles.length === 0 || ROLLEN.every((r) => roles.includes(r))) {
    return { customer: "PROSPECT", supplier: false };
  }
  const customer = roles.includes("PROSPECT") ? "PROSPECT" : roles.includes("CUSTOMER") ? "ACTIVE" : null;
  return { customer, supplier: roles.includes("SUPPLIER") };
}

/** Die Rumpffelder der Wahl (P2): `status` der Kundenrolle, `supplierStatus` der Lieferantenrolle. */
export function rolesOf(c: RoleChoice): { status: "ACTIVE" | "PROSPECT" | null; supplierStatus: "ACTIVE" | null } {
  return { status: c.customer, supplierStatus: c.supplier ? "ACTIVE" : null };
}

export const hasRole = (c: RoleChoice): boolean => c.customer !== null || c.supplier;

function valuesOf(c: RoleChoice): string[] {
  return [c.customer === "ACTIVE" ? "CUSTOMER" : c.customer === "PROSPECT" ? "PROSPECT" : null,
    c.supplier ? "SUPPLIER" : null].filter((w): w is string => w !== null);
}

/** Aus den angekreuzten Werten: Kommt zur einen Kundenstufe die andere dazu, gilt die neu angekreuzte. */
function choose(alt: RoleChoice, werte: readonly string[]): RoleChoice {
  const stufen = (["CUSTOMER", "PROSPECT"] as const).filter((s) => werte.includes(s));
  const neu = stufen.find((s) => !valuesOf(alt).includes(s)) ?? stufen[0];
  return { customer: neu === undefined ? null : CUSTOMER_STAGE[neu], supplier: werte.includes("SUPPLIER") };
}

export function RoleChoiceField(p: {
  wert: RoleChoice; onWert: (c: RoleChoice) => void; fehler?: string | undefined; gesperrt?: boolean | undefined;
}): ReactElement {
  return (
    <Optionen mehrfach richtung="waagerecht" beschriftung={PARTNER_TEXT.roleChoice} wert={valuesOf(p.wert)}
      {...(p.fehler === undefined ? {} : { fehler: p.fehler })}
      {...(hasRole(p.wert) ? {} : { hilfe: PARTNER_TEXT.atLeastOne })}
      {...(p.gesperrt === true ? { gesperrt: true } : {})}
      onGeaendert={(d) => p.onWert(choose(p.wert, d.werte ?? []))}>
      <option value="CUSTOMER">{PARTNER_TEXT.roleCustomer}</option>
      <option value="PROSPECT">{PARTNER_TEXT.roleProspect}</option>
      <option value="SUPPLIER">{PARTNER_TEXT.roleSupplier}</option>
    </Optionen>
  );
}
